import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, YAxis, ResponsiveContainer } from "recharts";
import { getPrices } from "@/lib/api";
import { getFbtcPrice } from "@/lib/fbtcTrade";
import { TokenLogo } from "@/components/common/TokenLogo";

const REFRESH_MS = 30000;
const CORE = new Set(["FMI", "USDT", "FBTC"]);
const getFeed = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/prices`).then((r) => r.json());
const getHistory = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/prices/history`).then((r) => r.json());
const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: v < 10 ? 6 : 2 })}`);

export function useLivePrices() {
  const fsc = useQuery({ queryKey: ["prices"], queryFn: getPrices, refetchInterval: REFRESH_MS });
  const fbtc = useQuery({ queryKey: ["fbtcPrice"], queryFn: getFbtcPrice, refetchInterval: REFRESH_MS });
  const feed = useQuery({ queryKey: ["priceFeed"], queryFn: getFeed, refetchInterval: REFRESH_MS });
  const history = useQuery({ queryKey: ["priceHistory"], queryFn: getHistory, refetchInterval: 5 * 60000 });
  const community = Object.values(feed.data?.prices || {}).filter((p) => !CORE.has(p.symbol))
    .map((p) => ({ symbol: p.symbol, address: p.address, name: p.name || p.note || p.symbol, price: p.usd, source: p.source }));
  const rows = [
    { symbol: "FMI", name: "FMI", price: fsc.data?.FMI?.usd, source: "FSC FMI/USDT pool" },
    { symbol: "USDT", name: "USDT (FSC test token, not Tether)", price: fsc.data?.USDT?.usd, source: "Fixed at $1 on FSC" },
    { symbol: "FBTC", name: "FBTC (not BTC-backed)", price: fbtc.data?.btcUsd, source: `Live BTC/USD rate${fbtc.data?.source ? ` · ${fbtc.data.source}` : ""}` },
    ...community,
  ].map((r) => ({ ...r, history: history.data?.tokens?.[r.symbol] }));
  return { rows, loading: fsc.isLoading || fbtc.isLoading, updatedAt: Math.max(fsc.dataUpdatedAt, fbtc.dataUpdatedAt) };
}

export const PriceStrip = () => {
  const { rows } = useLivePrices();
  return (
    <Link to="/prices" data-testid="price-strip" className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 rounded-xl border border-[#2A2411] bg-[#0A0A0A] hover:border-[#D4AF37]/30 transition-colors">
      <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Live prices</span>
      {rows.map((r) => (
        <span key={r.symbol} className="flex items-center gap-1.5 text-sm">
          <TokenLogo symbol={r.symbol} address={r.address} size="sm" />
          <span className="text-zinc-400">{r.symbol}</span>
          <span data-testid={`price-strip-${r.symbol.toLowerCase()}`} className="font-mono text-white">{usd(r.price)}</span>
        </span>
      ))}
    </Link>
  );
};

const Change = ({ symbol, value }) => {
  if (value == null) return <p data-testid={`price-change-${symbol}`} className="text-[11px] text-zinc-600">24h —</p>;
  const up = value >= 0;
  return (
    <p data-testid={`price-change-${symbol}`} className={`text-xs font-mono ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? "+" : ""}{value.toFixed(2)}% 24h
    </p>
  );
};

const Sparkline = ({ symbol, points }) => {
  if (!points || points.length < 2) {
    return <div data-testid={`price-chart-${symbol}`} className="w-32 h-10 hidden sm:flex items-center justify-center text-[10px] text-zinc-600">Chart building…</div>;
  }
  const up = points[points.length - 1][1] >= points[0][1];
  const data = points.map(([t, v]) => ({ t, v }));
  return (
    <div data-testid={`price-chart-${symbol}`} className="w-32 h-10 hidden sm:block">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Line type="monotone" dataKey="v" stroke={up ? "#34d399" : "#f87171"} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const PriceRow = ({ row }) => {
  const sym = row.symbol.toLowerCase();
  return (
    <div data-testid={`price-row-${sym}`} className="flex items-center justify-between gap-4 p-4 rounded-xl border border-[#2A2411] bg-[#0A0A0A]">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <TokenLogo symbol={row.symbol} address={row.address} />
        <div className="min-w-0">
          <p className="font-display font-bold text-white">{row.symbol}</p>
          <p className="text-xs text-zinc-500 truncate">{row.name}</p>
        </div>
      </div>
      <Sparkline symbol={sym} points={row.history?.points} />
      <div className="text-right min-w-[120px]">
        <p data-testid={`price-value-${sym}`} className="font-display text-xl font-bold text-[#D4AF37]">{usd(row.price)}</p>
        <Change symbol={sym} value={row.history?.change24h} />
        <p className="text-[11px] text-zinc-500">{row.source}</p>
      </div>
    </div>
  );
};
